import React from 'react'

import { useHistory, useRouteMatch } from 'react-router-dom'

import routes from 'routes/routes'
import * as service from 'service'
import AuthContext from 'contexts/AuthContext'

const AuthProvider = ({ children }) => {
  const history = useHistory()
  const matchRouteLoginMain = useRouteMatch(routes.login.main)
  const matchRouteLoginToken = useRouteMatch(routes.login.token)
  const [loaded, setLoaded] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [user, setUser] = React.useState(null)
  const [seller, setSeller] = React.useState(null)
  const [permissions, setPermissions] = React.useState(null)

  React.useEffect(() => {
    loginWithTokenCached()
    // eslint-disable-next-line
  }, [])

  const checkMainRoute = permissions => {
    const pages = [
      { path: routes.contracts.all, permission: 'index_contract' },
      { path: routes.invoices.all, permission: 'index' },
      { path: routes.clients.all, permission: 'index_customer' },
      { path: routes.plans.all, permission: 'index_plan' },
    ]

    if (!permissions.length) {
      return pages[0].path
    }

    const page = pages.find(
      route =>
        !route.path ||
        permissions.some(
          permission =>
            permission.active && permission.tag === route.permission,
        ),
    )

    return page ? page.path : routes.settings.all
  }

  const loadData = async () => {
    const userId = service.storage.getUserIdFromToken()
    const [
      responseUser,
      responseSeller,
      responsePermissions,
    ] = await Promise.all([
      service.recurrence.users.get({
        userId,
      }),
      service.recurrence.sellers.info(),
      service.recurrence.users.permissions({
        userId,
      }),
    ])

    setUser(responseUser.data.user)
    setSeller(responseSeller.data.seller)
    setPermissions(responsePermissions.data.profilePermissions)

    return {
      user: responseUser.data.user,
      seller: responseSeller.data.seller,
      permissions: responsePermissions.data.profilePermissions,
    }
  }

  const resetAuthData = () => {
    setLoaded(false)
    setIsLoading(false)
    setUser(null)
    setSeller(null)
    setPermissions(null)
  }

  const loginWithTokenCached = async () => {
    if (service.storage.hasRecurrenceAuthToken()) {
      setIsLoading(true)
      setLoaded(false)

      const data = await loadData()

      const isRouteLoginMain =
        matchRouteLoginMain && matchRouteLoginMain.isExact
      const isRouteLoginToken =
        matchRouteLoginToken && matchRouteLoginToken.isExact

      setTimeout(() => {
        setLoaded(true)
        setIsLoading(false)
      }, 100)

      const route = checkMainRoute(data.permissions)

      if (isRouteLoginMain) {
        history.push(route)
      }

      if (!isRouteLoginToken) {
        return
      }

      history.push(route)
    }
  }

  const login = async user => {
    setIsLoading(true)
    setLoaded(false)

    // get token and save
    const response = await service.recurrence.auth.login(user)
    service.storage.setRecurrenceAuthToken(response.data.authToken)

    // get data and redirect route
    const data = await loadData()
    const route = checkMainRoute(data.permissions)
    history.push(route)

    setTimeout(() => {
      setLoaded(true)
      setIsLoading(false)
    }, 100)
  }

  const logout = () => {
    resetAuthData()

    service.storage.removeRecurrenceAuthToken()
    history.push(routes.root)
  }

  return (
    <AuthContext.Provider
      value={{
        loaded,
        isLoading,
        user,
        seller,
        permissions,
        loadData,
        checkMainRoute,
        resetAuthData,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
